/* gsap */
/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  useState,
  useEffect,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from 'react';
import { TransitionGroup } from 'react-transition-group';
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useLocation,
  useParams,
  type Location,
} from 'react-router';
import NodeTransition from 'components/NodeTransition/NodeTransition';
import Loader from 'components/Loader/Loader';
import Logo from 'components/Icons/Logo/Logo';
import NotFound from './NotFound/NotFound';
import Index from './Index/Index';
import Entry from './Entry/Entry';
import Next from './Entry/Next/Next';
import Error from './Error/Error';
import Authorize from './Authorize/Authorize';
import { useOpacityScroll } from './animations';
import useData from './data';
import useImagePreload from './imagePreload';
import './ProgramApp.scss';

const DEFAULT_AUTHORIZE: NonNullable<ProgramAppProps['authorize']> = {};

const DEFAULT_LABELS = {
  name: 'Digital program',
  next: 'Next story',
};

// Like Reach Router, the first route shown when the app loads doesn't take focus.
let initialRoute = true;

/**
 * Wrap a route in a focusable element and move focus to it after navigation,
 * so keyboard and screen reader users start at the top of the new page.
 *
 * `rootRef` comes from NodeTransition, which animates this same element.
 */
function RouteFocus({
  rootRef,
  children,
}: {
  rootRef: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  useEffect(() => {
    if (initialRoute) {
      initialRoute = false;
      return;
    }
    rootRef.current!.focus();
  }, []);

  return (
    <div
      ref={rootRef as RefObject<HTMLDivElement | null>}
      tabIndex={-1}
      style={{ outline: 'none' }}
    >
      {children}
    </div>
  );
}

/**
 * Call `children` with the current router location.
 */
function WithLocation({
  children,
}: {
  children: (location: Location) => ReactNode;
}) {
  return children(useLocation());
}

interface AppEntry {
  id: string | number;
  nav: string;
  url: string;
  slug: string;
  // The rest is the entry's own props (Entry), as the API sends them.
  [prop: string]: unknown;
}

/** What the data API answers with; useData does not type it (it is `ProgramData`). */
interface AppData extends Omit<
  ComponentProps<typeof Index>,
  'nav' | 'labels' | 'onMount' | 'onTheme' | 'onNavigate'
> {
  name: string;
  theme: string;
  locked?: boolean;
  purchased?: boolean;
  sku: string;
  price: number | string;
  basket: string;
  entries: AppEntry[];
}

interface ProgramAppProps {
  credentials: {
    clientAction: string;
    loginAction: string;
    dataAction: string;
    defaultClient?: object;
  };
  listUrl?: string;
  homeUrl?: string;
  authorize?: Partial<ComponentProps<typeof Authorize>>;
  labels?: { name: string; next: string };
}

/**
 * This is the ProgramApp component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function ProgramApp({
  credentials,
  listUrl = '/',
  homeUrl = '/',
  authorize: authorizeConfig = DEFAULT_AUTHORIZE,
  labels = DEFAULT_LABELS,
}: ProgramAppProps) {
  // `credentials` has no itixUrl, so useData gets undefined for it (as before the conversion).
  const [data, status, message] = useData(
    credentials as unknown as Parameters<typeof useData>[0]
  ) as unknown as readonly [AppData | null, number | null, string | null];
  const [theme, setTheme] = useState<string | null>(null);
  const [view, setView] = useState<'index' | 'entry'>('index');
  const [title, setTitle] = useState(false);
  const ref = useOpacityScroll<HTMLAnchorElement>();

  useImagePreload(data as Parameters<typeof useImagePreload>[0]);

  // The program labels hold only name and next, while Entry, Index and Next also read
  // menu, open, close and more (which are undefined here, as before the conversion).
  const pageLabels = labels as unknown as ComponentProps<
    typeof Entry
  >['labels'];
  const nextLabels = labels as unknown as ComponentProps<typeof Next>['labels'];

  useEffect(() => {
    if (data) {
      setTheme(data.theme);
    }
  }, [data]);

  useEffect(() => {
    document.body.classList.add(`theme-${theme}`);

    return () => {
      document.body.classList.remove(`theme-${theme}`);
    };
  }, [theme]);

  function renderTitle() {
    if (!title) return false;
    return <small>{data!.name}</small>;
  }

  function renderContent() {
    if (status === 500) return <Error>{message ?? undefined}</Error>;
    if (status === 404) return <NotFound />;
    if (!data) return <Loader key="loader" />;

    const nav = data.entries.map(({ id, nav, url }) => ({
      id,
      text: nav
        .replace('<br>', '')
        .replace('<br />', '')
        .replace('\n', '')
        .replace('  ', ' '),
      url,
    }));

    const EntryProxy = () => {
      const { slug: routeSlug } = useParams();

      useEffect(() => {
        setTitle(true);
        setView('entry');
      }, []);

      if (!data) return null;

      if (!routeSlug) return null;

      const index = data.entries.findIndex(({ slug }) => slug === routeSlug);
      const next = index + 1 < data.entries.length ? index + 1 : 0;

      return (
        <Entry
          key={`entry-${index}`}
          {...data.entries[index]}
          nav={nav}
          next={{
            ...data.entries[next],
            labels: nextLabels,
          }}
          labels={pageLabels}
          onTheme={theme => {
            setTheme(theme);
          }}
        />
      );
    };

    return (
      <WithLocation key="content">
        {location => (
          <TransitionGroup>
            <NodeTransition
              key={`${location.pathname}${location.search}${location.hash}`}
              timeout={1500}
              onEnter={() => {
                document.body.classList.add('transitioning');
              }}
              onEntered={() => {
                document.body.classList.remove('transitioning');
              }}
            >
              {nodeRef => (
                <RouteFocus rootRef={nodeRef}>
                  <Routes location={location}>
                    <Route
                      path="/:program"
                      element={
                        <Index
                          key="index"
                          {...data}
                          nav={nav}
                          labels={pageLabels}
                          onMount={() => {
                            setTitle(false);
                            setView('index');
                          }}
                          onTheme={theme => {
                            setTheme(theme);
                          }}
                        />
                      }
                    />
                    <Route path="/:program/:slug" element={<EntryProxy />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </RouteFocus>
              )}
            </NodeTransition>
          </TransitionGroup>
        )}
      </WithLocation>
    );
  }

  function renderLogin() {
    if (!data) return null;
    if (!data.locked) return null;
    if (data.purchased) return null;

    return (
      <Authorize
        key="authorize"
        {...authorizeConfig}
        sku={data.sku}
        name={data.name}
        price={data.price}
        basket={data.basket}
      />
    );
  }

  function renderHomeLink() {
    if (view === 'index') {
      return (
        <a ref={ref} className="logo" href={listUrl}>
          <Logo />
          {renderTitle()}
        </a>
      );
    }

    return (
      <Link ref={ref} className="logo" to={homeUrl}>
        <Logo />
        {renderTitle()}
      </Link>
    );
  }

  return (
    <BrowserRouter>
      {renderHomeLink()}
      {renderContent()}
      {renderLogin()}
    </BrowserRouter>
  );
}

export default ProgramApp;
